import React, { useEffect, useState, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FieldControl, FieldGroup, FormBuilder } from "react-reactive-form";
import DashboardContent from "../../components/DashboardContent";
import useCsr from "../../hooks/useCsr";
import RedirectHandler from "../../components/RedirectHandler";
import { CsrAPI } from "../../apis/CsrAPI";
import { CircleLoader } from "react-spinners";
import { CButton, CFormTextarea } from "@coreui/react";
import { ReactComponent as FilterIcon } from "../../assets/images/icons/filter.svg";
import { ReactComponent as EditDraft } from "../../assets/images/icons/edit-draft.svg";
import { ReactComponent as CopyIcon } from "../../assets/images/icons/copy.svg";
import { ReactComponent as CheckCircleIcon } from "../../assets/images/icons/check-circle.svg";
import { ReactComponent as WarningIcon } from "../../assets/images/icons/csr-warning.svg";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

const formatGenerated = generated => {
  if (!generated) return "";
  const d = new Date(generated);
  const hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";
  const hours12 = hours % 12 || 12;
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  return `${month} ${day} ${year} - ${hours12}:${minutes} ${ampm}`;
};

const TableAnalysisAIResponse = () => {
  const { studyId, tableId } = useParams();
  const { state: locationState } = useLocation();
  const selectedTableNameFromStore = useSelector(
    state => state.selectedTable.selectedTableName
  );

  const { wrongEnvironment } = useCsr();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const tableName =
    locationState?.tableName ?? selectedTableNameFromStore ?? data.tableTitle;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isIncompleteAnalysis, setIsIncompleteAnalysis] = useState(false);
  const [copied, setCopied] = useState(false);

  const [editForm] = useState(() => FormBuilder.group({ content: [""] }));

  const fetchOutput = useCallback(async () => {
    if (!studyId || !tableId) {
      setLoading(false);
      setError("Missing study or table ID");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const isReady = statusRes => {
        const status = (statusRes?.status ?? statusRes?.request_status ?? "")
          .toString()
          .toLowerCase()
          .trim();
        return status === "ready" || status === "ready_with_errors";
      };

      let statusRes = await CsrAPI.getTableRequestStatus({
        studyId,
        tableId,
      });
      while (!isReady(statusRes)) {
        await new Promise(resolve => setTimeout(resolve, 5000));
        statusRes = await CsrAPI.getTableRequestStatus({
          studyId,
          tableId,
        });
      }

      const result = await CsrAPI.getTableOutput({ studyId, tableId });
      setData({
        content: result?.output,
        // status: "Generated",
        status: result?.result_type || result?.error_code,
        // generated: new Date(),
        generated: result?.timestamp,
        tableTitle: result?.table_title,
      });
      if (result?.result_type && result?.result_type !== "success") {
        setIsIncompleteAnalysis(true);
      }
      editForm.get("content").setValue(result?.body?.response_text ?? "");
    } catch (err) {
      toast.error("Failed to load AI response. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [studyId, tableId, editForm]);

  useEffect(() => {
    fetchOutput();
  }, [fetchOutput]);

  const handleEditAnalysisCriteria = () => {
    setData({
      content: "We’re generating your AI response",
      status: "generating",
      generated: new Date(),
    });
    navigate(`/csr/${studyId}/table-analysis-questions/${tableId}`, {
      state: tableName ? { tableName } : undefined,
    });
  };

  const handleEnterEditMode = () => {
    editForm.get("content").setValue(data?.content ?? "");
    setIsEditMode(true);
  };

  const handleCancel = () => {
    editForm.get("content").setValue(data?.content ?? "");
    setIsEditMode(false);
  };

  const handleSave = async () => {
    if (!studyId || !tableId || saving) return;
    const content = editForm.get("content").value ?? "";
    setSaving(true);
    try {
      await CsrAPI.putEditedResponse({
        studyId,
        tableId,
        editedContent: content,
      });
      setData(prev => (prev ? { ...prev, content } : { content }));
      setIsEditMode(false);
    } catch (err) {
      console.error("Failed to save:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleCopyText = () => {
    const text = isEditMode
      ? (editForm.get("content").value ?? "")
      : (data?.content ?? "");
    if (text) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (wrongEnvironment) {
    return <RedirectHandler />;
  }

  if (error && !data) {
    return (
      <DashboardContent
        sidebarCSR={true}
        headerTitle="Review generated AI response"
        headerButton={false}
        pageContent={
          <div className="table-analysis-ai-response">
            <p className="text-danger">{error || "No data available"}</p>
          </div>
        }
      />
    );
  }

  const sectionTitle = data?.tableTitle || data?.sectionTitle || "";
  const displayTitle = tableName || sectionTitle;

  const ContentAreaLoader = () => (
    <div
      className="table-analysis-ai-response__content table-analysis-ai-response__content--loading d-flex flex-column align-items-center justify-content-center p-4 rounded"
      style={{ whiteSpace: "pre-wrap", minHeight: 400 }}>
      <CircleLoader color="#0CD2B6" size={48} />
      <p className="mt-3 mb-1 fw-semibold">
        We&apos;re generating your AI response
      </p>
      <p className="text-muted small mb-0">
        You can close this window by clicking Dashboard button
      </p>
    </div>
  );

  const PageContent = () => (
    <>
      <div className="ai-page-title">Review response</div>
      <div className="table-analysis-ai-response">
        {/* Header: table name (left) + Generated label (right) */}
        <div className="table-analysis-ai-response__header d-flex justify-content-between align-items-start gap-4 mb-4">
          <h2 className="table-analysis-ai-response__title mb-0">
            {displayTitle}
          </h2>
          <div className="table-analysis-ai-response__generated d-flex align-items-center gap-2">
            <CheckCircleIcon width={24} height={24} className="flex-shrink-0" />
            <span className="table-analysis-ai-response__generated-text">
              <span className="fw-semibold">
                {loading ? "Generating" : "Generated"}
              </span>
              <span className="text-muted">
                : {loading ? "…" : formatGenerated(data?.generated) || "—"}
              </span>
            </span>
          </div>
        </div>

        {/* Response container with gradient border */}
        <div className="table-analysis-ai-response__container">
          <div
            className="table-analysis-ai-response__gradient"
            style={{
              backgroundImage:
                "linear-gradient(90deg, rgb(255, 200, 87) 1.82%, rgb(124, 36, 192) 42.05%, rgb(12, 210, 182) 87.3%)",
            }}
          />
          <div className="table-analysis-ai-response__inner p-4">
            {/* Buttons row: Edit draft + Copy text (left) | Edit criteria (right) */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex gap-3 align-items-center">
                {!isEditMode ? (
                  <>
                    <CButton
                      className="table-analysis-ai-response__btn-edit-draft d-flex align-items-center gap-2"
                      onClick={handleEnterEditMode}
                      disabled={loading}>
                      <EditDraft width={20} height={20} />
                      Edit draft
                    </CButton>
                    <CButton
                      className="table-analysis-ai-response__btn-copy d-flex align-items-center gap-2"
                      onClick={handleCopyText}
                      disabled={loading}>
                      {copied ? (
                        <CheckCircleIcon width={20} height={20} />
                      ) : (
                        <CopyIcon width={20} height={20} />
                      )}
                      Copy text
                    </CButton>
                  </>
                ) : (
                  <div className="d-flex gap-2">
                    <CButton
                      color="link"
                      variant="ghost"
                      size="sm"
                      onClick={handleCancel}
                      disabled={saving}>
                      Cancel
                    </CButton>
                    <CButton
                      color="primary"
                      size="sm"
                      onClick={handleSave}
                      disabled={saving}>
                      {saving ? "Saving…" : "Save"}
                    </CButton>
                  </div>
                )}
              </div>
              <CButton
                className="table-analysis-ai-response__btn-edit-criteria d-flex align-items-center gap-2"
                onClick={handleEditAnalysisCriteria}>
                <FilterIcon width={20} height={20} />
                Edit criteria
              </CButton>
            </div>

            {/* Incomplete Analysis warning */}
            {!loading && isIncompleteAnalysis && (
              <div className="table-analysis-ai-response__warning d-flex gap-2 align-items-start p-3 mb-4 rounded">
                <WarningIcon width={24} height={24} className="flex-shrink-0" />
                <span className="table-analysis-ai-response__warning-text">
                  {/* <strong>Incomplete Analysis:</strong>{" "} */}
                  <strong>Status:</strong>{" "}
                  {typeof data?.status === "string"
                    ? data.status
                    : `Unable to detect some endpoint in Table [${tableId}]. The AI has generated a partial summary based on available data.`}
                </span>
              </div>
            )}

            {loading ? (
              <ContentAreaLoader />
            ) : isEditMode ? (
              <FieldGroup
                control={editForm}
                render={() => (
                  <FieldControl
                    name="content"
                    render={({ handler }) => (
                      <CFormTextarea
                        {...handler()}
                        rows={12}
                        className="w-100"
                        style={{ border: "1px solid rgba(102, 126, 234, 0.3)" }}
                      />
                    )}
                  />
                )}
              />
            ) : (
              <div
                className="table-analysis-ai-response__content p-4 rounded"
                style={{ whiteSpace: "pre-wrap", minHeight: 400 }}>
                {data?.content || ""}
              </div>
            )}

            {!loading && data && (
              <p className="table-analysis-ai-response__disclaimer mt-4 mb-0">
                This response was created by Generative AI. Please review for
                clinical accuracy and refine the text as needed.
              </p>
            )}
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={true}
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle=""
      headerButton={false}
      pageContent={PageContent()}
      nextButton={{
        buttonTitle: "Dashboard",
        buttonGoToAction: "/csr/" + studyId + "/advancedsearch",
      }}
      nextButtonRightArrow={false}
    />
  );
};

export default TableAnalysisAIResponse;
